import React, { useEffect, useCallback, memo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import LinearGradient from "react-native-linear-gradient";
import { StyleSheet, View, TouchableOpacity, FlatList } from "react-native";

//Custom Imports
import color from "@constants/color";
import { scale } from "@utils/scale";
import t from "@constants/localization";
import contestEvents from "../ContestEvents";
import BackArrow from "@assets/svg/BackArrow";
import CustomText from "@components/CustomText";
import RenderContestEntity from "../FetchContestEntity";
import contestAction from "@actions/contest.action";
import CircularLoader from "@screens/Story/CircularLoader";
import WatchUpcoming from "@assets/svg/Contest/WatchUpcoming";
import { contestDetailsSelector } from "@selectors/contest.selector";

const PATH = "screens/Contest";
const userSelector = (state) => state?.auth?.user;

const ContestOverview = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const {
    user_image = "",
    name = "",
    language = null,
  } = useSelector(userSelector);
  const { data = {}, api = {} } = useSelector(contestDetailsSelector);
  const { pending = false } = api || {};
  const {
    current_slab_index,
    list_items,
    ordered_cx,
    total_cx,
    contest_start_date = null,
    performance = "",
    contest_ended = false,
    can_only_view = false,
    contest_name = "",
  } = data;

  const [journeyVisible, setJourneyVisible] = useState(false);

  //Params Requireds
  const { contest_id = 0, isPastContest = false } = route?.params || {};

  useEffect(() => {
    dispatch(
      contestAction.getContestDetailsContest.pending({ contest_id }, null),
    );
  }, []);

  const onBackClick = () => {
    navigation.goBack();
  };

  const _keyExtractor = (item, index) => `${item?.title}${index}`;

  const _renderItem = ({ item, index }) => {
    const { type = "" } = item;
    if (type) {
      return (
        <RenderContestEntity
          trackEvent={contestEvents}
          route={route}
          navigation={navigation}
          type={type}
          index={index}
          current_slab_index={current_slab_index}
          total_cx={total_cx}
          ordered_cx={ordered_cx}
          contest_id={contest_id}
          tl_image={user_image}
          tl_name={name}
          isPastContest={isPastContest}
          journeyVisible={journeyVisible}
          contest_start_date={contest_start_date}
          language={language}
          performance={performance}
          contest_ended={contest_ended}
          can_only_view={can_only_view}
          {...item}
        />
      );
    } else {
      return null;
    }
  };

  const _onViewableItemsChanged = useCallback(({ viewableItems }) => {
    if (viewableItems.length !== 0) {
      viewableItems.forEach((element) => {
        if (element?.item?.type === "YOUR_JOURNEY" && !journeyVisible)
          setJourneyVisible(true);
      });
    }
  }, []);

  return (
    <React.Fragment>
      {pending ? (
        <View style={styles.loaderStyle}>
          <CircularLoader />
        </View>
      ) : (
        <View style={styles.container}>
          <LinearGradient
            colors={[color.skyBlue, color.rgbaBlue]}
            style={styles.gradientContainer}
          >
            <View style={styles.headerTitleContainer}>
              <TouchableOpacity activeOpacity={0.8} onPress={onBackClick}>
                <BackArrow />
              </TouchableOpacity>
              <CustomText
                variant="title_20"
                newfont={true}
                numberOfLines={1}
                style={styles.textLeftPadding}
              >
                {contest_name ? contest_name : t(`${PATH}/Contest`)}
              </CustomText>
            </View>
          </LinearGradient>
          <FlatList
            renderAheadOffset={10}
            data={list_items}
            renderItem={_renderItem}
            keyExtractor={_keyExtractor}
            showsVerticalScrollIndicator={false}
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.contentContainer}
            onViewableItemsChanged={_onViewableItemsChanged}
            viewabilityConfig={{ itemVisiblePercentThreshold: 50 }}
          />
          {contest_start_date ? (
            <View style={styles.finalDateContainer}>
              <WatchUpcoming color={"#6FB7D6"} />
              <CustomText
                variant="title_20"
                newfont={true}
                numberOfLines={1}
                style={styles.finalDateText}
              >
                {contest_start_date?.time_flag
                  ? `${t(`${PATH}/remainingTimeMessage`, {
                      time: contest_start_date?.final_date,
                    })}`
                  : `${contest_start_date?.final_date} ${t(
                      `${PATH}/daysToGo`,
                    )}`}
              </CustomText>
            </View>
          ) : null}
        </View>
      )}
    </React.Fragment>
  );
};

export default memo(ContestOverview);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: color.white,
  },
  contentContainer: {
    flexGrow: 1,
    paddingBottom: 70,
    backgroundColor: color.white,
  },
  gradientContainer: {
    paddingHorizontal: scale(16),
  },
  headerTitleContainer: {
    alignItems: "center",
    flexDirection: "row",
    height: 56,
  },
  textLeftPadding: {
    paddingLeft: scale(23),
    color: color.mediumBlack,
  },
  loaderStyle: {
    position: "absolute",
    alignSelf: "center",
    zIndex: 100,
  },
  finalDateContainer: {
    position: "absolute",
    bottom: 0,
    zIndex: 100,
    backgroundColor: color.black,
    width: "100%",
    height: 64,
    justifyContent: "center",
    alignItems: "center",
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    flexDirection: "row",
  },
  finalDateText: {
    color: color.white,
    marginStart: 4,
  },
});
